import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, MapPin, Briefcase, User, Mail, Phone, Filter, X } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const DirectoryPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    membership_type: "",
    speciality_id: "",
    state: "",
  });
  const [specialities, setSpecialities] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, page_size: 25 });
  const [showFilters, setShowFilters] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [noResultsFromSearch, setNoResultsFromSearch] = useState(false);

  const membershipTypes = [
    { value: "fellow", label: "Fellow" },
    { value: "professional", label: "Professional" },
    { value: "associate", label: "Associate" },
    { value: "student", label: "Student" },
    { value: "professional_international", label: "Professional (Intl.)" },
    { value: "associate_international", label: "Associate (Intl.)" },
  ];

  const states = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
    "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
    "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
    "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
    "Uttarakhand", "West Bengal"
  ];

  useEffect(() => {
    fetchSpecialities();
    fetchMembers();
  }, [filters, pagination.page]);

  const fetchSpecialities = async () => {
    try {
      const response = await axios.get(`${API}/admin/specialities`);
      setSpecialities(response.data);
    } catch (error) {
      console.error("Error fetching specialities:", error);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: pagination.page_size,
      });
      
      if (filters.membership_type) params.append("membership_type", filters.membership_type);
      if (filters.speciality_id) params.append("speciality_id", filters.speciality_id);
      if (filters.state) params.append("state", filters.state);
      if (searchTerm) params.append("search", searchTerm);
      if (showAll) params.append("show_all", "true");
      
      const response = await axios.get(`${API}/directory?${params}`, { withCredentials: true });
      setMembers(response.data.members);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
      
      // Track if this was a search that returned no results
      const hadSearchCriteria = filters.membership_type || filters.speciality_id || filters.state || searchTerm;
      setNoResultsFromSearch(hadSearchCriteria && response.data.members.length === 0);
    } catch (error) {
      console.error("Error fetching directory:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowAll(false);
    fetchMembers();
  };

  const handleShowAll = () => {
    setShowAll(true);
    clearFilters();
  };

  const clearFilters = () => {
    setFilters({ membership_type: "", speciality_id: "", state: "" });
    setSearchTerm("");
    setNoResultsFromSearch(false);
  };

  const hasActiveFilters = filters.membership_type || filters.speciality_id || filters.state || searchTerm;

  return (
    <div className="min-h-screen bg-white" data-testid="directory-page">
      {/* Hero Section */}
      <section className="bg-slate-50 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading font-bold text-4xl text-slate-900 mb-4 tracking-tight">
              Member Directory
            </h1>
            <p className="text-lg text-slate-600">
              Find and connect with verified mental health professionals across India
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-8 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, organization, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
                data-testid="directory-search"
              />
            </div>
            
            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center gap-3">
              <Select
                value={filters.membership_type}
                onValueChange={(value) => setFilters({ ...filters, membership_type: value })}
              >
                <SelectTrigger className="w-[160px] h-11" data-testid="filter-membership">
                  <SelectValue placeholder="Membership" />
                </SelectTrigger>
                <SelectContent>
                  {membershipTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                value={filters.speciality_id}
                onValueChange={(value) => setFilters({ ...filters, speciality_id: value })}
              >
                <SelectTrigger className="w-[180px] h-11" data-testid="filter-speciality">
                  <SelectValue placeholder="Speciality" />
                </SelectTrigger>
                <SelectContent>
                  {specialities.map((spec) => (
                    <SelectItem key={spec.id} value={spec.id}>
                      {spec.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                value={filters.state}
                onValueChange={(value) => setFilters({ ...filters, state: value })}
              >
                <SelectTrigger className="w-[160px] h-11" data-testid="filter-state">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 h-11" data-testid="search-btn">
                Search
              </Button>
              
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleShowAll}
                className="h-11"
                data-testid="show-all-members"
              >
                Browse Members
              </Button>
              
              {hasActiveFilters && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="h-11"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className="flex-1"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800">
                Search
              </Button>
            </div>
          </form>
          
          {/* Mobile Filters Panel */}
          {showFilters && (
            <div className="lg:hidden mt-4 p-4 bg-slate-50 rounded-lg space-y-3">
              <Select
                value={filters.membership_type}
                onValueChange={(value) => setFilters({ ...filters, membership_type: value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Membership Type" />
                </SelectTrigger>
                <SelectContent>
                  {membershipTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                value={filters.speciality_id}
                onValueChange={(value) => setFilters({ ...filters, speciality_id: value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Speciality" />
                </SelectTrigger>
                <SelectContent>
                  {specialities.map((spec) => (
                    <SelectItem key={spec.id} value={spec.id}>
                      {spec.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                value={filters.state}
                onValueChange={(value) => setFilters({ ...filters, state: value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-slate-600">
              {loading ? "Loading..." : pagination.total > 0 ? `Showing 1–${Math.min(members.length, pagination.page_size)} of ${pagination.total} members` : ""}
            </p>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="border border-slate-200">
                  <CardContent className="p-6">
                    <div className="animate-pulse space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-slate-200"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="h-3 bg-slate-200 rounded"></div>
                      <div className="h-3 bg-slate-200 rounded w-2/3"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-16">
              <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">
                {noResultsFromSearch ? "No Matching Members Found" : "Search the Directory"}
              </h3>
              <p className="text-slate-600 mb-4">
                {noResultsFromSearch 
                  ? "Try adjusting your search criteria or browse all members"
                  : "Use the search bar or filters above, or click 'Browse Members' to see all verified professionals"}
              </p>
              <div className="flex gap-2 justify-center">
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                )}
                <Button onClick={handleShowAll} data-testid="show-all-empty-state">
                  Browse Members
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {members.map((member) => (
                <Link 
                  key={member.user_id} 
                  to={`/directory/${member.user_id}`}
                  data-testid={`member-card-${member.user_id}`}
                >
                  <Card className="border border-slate-200 hover:border-teal-200 hover:shadow-card-hover transition-all duration-300 h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                          {member.photo_url ? (
                            <img 
                              src={member.photo_url} 
                              alt={member.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <User className="w-8 h-8 text-teal-700" />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-lg text-slate-900 truncate">
                            {member.name}
                          </h3>
                          <p className="text-sm text-teal-700 font-medium capitalize">
                            {member.membership_type?.replace('_', ' ')} Member
                          </p>
                          <p className="text-sm text-slate-600 truncate">
                            {member.speciality || "Mental Health Professional"}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 space-y-2">
                        {member.organization && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{member.organization}</span>
                          </div>
                        )}
                        
                        {(member.city || member.state) && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="truncate">
                              {[member.city, member.state].filter(Boolean).join(", ")}
                            </span>
                          </div>
                        )}
                        
                        {member.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{member.email}</span>
                          </div>
                        )}
                        
                        {member.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span>{member.phone}</span>
                          </div>
                        )}
                      </div>
                      
                      {member.interests_excerpt && (
                        <p className="mt-4 text-sm text-slate-500 line-clamp-2">
                          {member.interests_excerpt}...
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {!loading && pagination.total > pagination.page_size && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                data-testid="prev-page"
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600 px-4">
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.page_size)}
              </span>
              <Button
                variant="outline"
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.page_size)}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                data-testid="next-page"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DirectoryPage;
